import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getHomePageContent, updateHomePageContent, HomePageContent } from '../services/adminApi';

const emptyContent: HomePageContent = {
  heroEyebrow: '',
  heroTitle: '',
  heroCopy: '',
  primaryCtaText: 'Request quote',
  primaryCtaLink: '/quote',
  secondaryCtaText: 'View portfolio',
  secondaryCtaLink: '/portfolio',
  heroImage: '/images/home-hero.svg',
  services: [
    { title: 'Design-build delivery', description: 'Integrated design and construction execution for large capital programs.' },
    { title: 'Renovations & upgrades', description: 'Reliable renovation planning that preserves operations and improves value.' },
    { title: 'Civil infrastructure', description: 'Heavy civil and utility delivery for complex campus and industrial sites.' },
  ],
  testimonials: [
    { quote: 'ABC Construction delivered without schedule delays and kept stakeholders informed every step.', author: 'Senior Owner, Industrial Developer' },
    { quote: 'The team managed a complex retrofit with strong safety controls and minimal disruption.', author: 'Facilities Director, Major Retailer' },
  ],
  partners: ['Comcast', 'AECOM', 'Skanska', 'Turner'],
  stats: [
    { label: 'Projects', value: '170+' },
    { label: 'Years', value: '32+' },
    { label: 'Safety', value: 'Zero LTIs in last 24 months' },
  ],
  carouselSlides: [
    { src: '/images/carousel-0.svg', alt: 'Industrial warehouse project', title: '325,000 SF Industrial Campus', description: 'Accelerated delivery with zero incidents and modular prefabrication.' },
    { src: '/images/carousel-1.svg', alt: 'Design-build office tower', title: 'Design-Build Office Tower', description: 'Integrated delivery reducing schedule risk and cost impact.' },
    { src: '/images/carousel-2.svg', alt: 'Sustainable retail renovation', title: 'Adaptive Retail Campus Renovation', description: 'Energy-efficient upgrades with operational continuity.' },
  ],
  showHero: true,
  showServices: true,
  showCarousel: true,
  showTestimonials: true,
  showStats: true,
  showPartners: true,
  showAboutPage: true,
  showPortfolioPage: true,
  showServicesPage: true,
  showCareersPage: true,
  showNewsPage: true,
  showContactPage: true,
  showFaqPage: true,
  showQuotePage: true,
  showLegalPage: true,
};

const HomePageEditor = () => {
  const { t } = useTranslation();
  const [model, setModel] = useState<HomePageContent>(emptyContent);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    getHomePageContent()
      .then((data) => {
        if (!data || Object.keys(data).length === 0) {
          setModel(emptyContent);
        } else {
          setModel({
            ...emptyContent,
            ...data,
            services: data.services?.length ? data.services : emptyContent.services,
            testimonials: data.testimonials?.length ? data.testimonials : emptyContent.testimonials,
            stats: data.stats?.length ? data.stats : emptyContent.stats,
            partners: data.partners?.length ? data.partners : emptyContent.partners,
            carouselSlides: data.carouselSlides?.length ? data.carouselSlides : emptyContent.carouselSlides,
            showHero: data.showHero ?? true,
            showServices: data.showServices ?? true,
            showCarousel: data.showCarousel ?? true,
            showTestimonials: data.showTestimonials ?? true,
            showStats: data.showStats ?? true,
            showPartners: data.showPartners ?? true,
            showAboutPage: data.showAboutPage ?? true,
            showPortfolioPage: data.showPortfolioPage ?? true,
            showServicesPage: data.showServicesPage ?? true,
            showCareersPage: data.showCareersPage ?? true,
            showNewsPage: data.showNewsPage ?? true,
            showContactPage: data.showContactPage ?? true,
            showFaqPage: data.showFaqPage ?? true,
            showQuotePage: data.showQuotePage ?? true,
            showLegalPage: data.showLegalPage ?? true,
          });
        }
      })
      .catch((err) => setError(err instanceof Error ? err.message : t('homepage.loadFailed')))
      .finally(() => setLoading(false));
  }, [t]);

  const onChange = (key: keyof HomePageContent, value: string | boolean | string[] | HomePageContent['services'][number][] | HomePageContent['testimonials'][number][] | HomePageContent['stats'][number][] | HomePageContent['carouselSlides'][number][]) => {
    setModel((current) => ({ ...current, [key]: value } as HomePageContent));
  };

  const onNestedChange = (key: keyof HomePageContent, index: number, field: string, value: string) => {
    setModel((current) => ({
      ...current,
      [key]: (current[key] as any[]).map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      ),
    } as HomePageContent));
  };

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSaved(false);

    try {
      await updateHomePageContent(model);
      setSaved(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('homepage.saveFailed'));
    }
  };

  if (loading) {
    return <div>{t('homepage.loading')}</div>;
  }

  return (
    <div className="page-grid" style={{ gap: '1.5rem' }}>
      <div className="section-heading">
        <h2>{t('homepage.title')}</h2>
        <p>{t('homepage.description')}</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {saved && <div className="alert alert-success">{t('homepage.savedMessage')}</div>}

      <form className="form-grid" onSubmit={handleSave}>
        <div className="form-group">
          <label htmlFor="hero-eyebrow">{t('homepage.heroEyebrow')}</label>
          <input
            id="hero-eyebrow"
            className="form-control"
            value={model.heroEyebrow}
            onChange={(e) => onChange('heroEyebrow', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="hero-title">{t('homepage.heroTitle')}</label>
          <input
            id="hero-title"
            className="form-control"
            value={model.heroTitle}
            onChange={(e) => onChange('heroTitle', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="hero-copy">{t('homepage.heroCopy')}</label>
          <textarea
            id="hero-copy"
            className="form-control"
            rows={4}
            value={model.heroCopy}
            onChange={(e) => onChange('heroCopy', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="hero-image">{t('homepage.heroImage')}</label>
          <input
            id="hero-image"
            className="form-control"
            value={model.heroImage}
            onChange={(e) => onChange('heroImage', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="primary-cta-text">{t('homepage.primaryCtaText')}</label>
          <input
            id="primary-cta-text"
            className="form-control"
            value={model.primaryCtaText}
            onChange={(e) => onChange('primaryCtaText', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="primary-cta-link">{t('homepage.primaryCtaLink')}</label>
          <input
            id="primary-cta-link"
            className="form-control"
            value={model.primaryCtaLink}
            onChange={(e) => onChange('primaryCtaLink', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="secondary-cta-text">{t('homepage.secondaryCtaText')}</label>
          <input
            id="secondary-cta-text"
            className="form-control"
            value={model.secondaryCtaText}
            onChange={(e) => onChange('secondaryCtaText', e.target.value)}
          />
        </div>

        <div className="form-group">
          <label htmlFor="secondary-cta-link">{t('homepage.secondaryCtaLink')}</label>
          <input
            id="secondary-cta-link"
            className="form-control"
            value={model.secondaryCtaLink}
            onChange={(e) => onChange('secondaryCtaLink', e.target.value)}
          />
        </div>

        <fieldset style={{ border: '1px solid #d1d5db', borderRadius: 12, padding: '1rem' }}>
          <legend>{t('homepage.visibilityLegend')}</legend>
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <input
                id="show-hero"
                type="checkbox"
                checked={model.showHero}
                onChange={(e) => onChange('showHero', e.target.checked)}
              />
              {t('homepage.heroVisible')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <input
                id="show-services"
                type="checkbox"
                checked={model.showServices}
                onChange={(e) => onChange('showServices', e.target.checked)}
              />
              {t('homepage.servicesVisible')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <input
                id="show-carousel"
                type="checkbox"
                checked={model.showCarousel}
                onChange={(e) => onChange('showCarousel', e.target.checked)}
              />
              {t('homepage.carouselVisible')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <input
                id="show-testimonials"
                type="checkbox"
                checked={model.showTestimonials}
                onChange={(e) => onChange('showTestimonials', e.target.checked)}
              />
              {t('homepage.testimonialsVisible')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <input
                id="show-stats"
                type="checkbox"
                checked={model.showStats}
                onChange={(e) => onChange('showStats', e.target.checked)}
              />
              {t('homepage.statsVisible')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <input
                id="show-partners"
                type="checkbox"
                checked={model.showPartners}
                onChange={(e) => onChange('showPartners', e.target.checked)}
              />
              {t('homepage.partnersVisible')}
            </label>
          </div>
        </fieldset>

        <fieldset style={{ border: '1px solid #d1d5db', borderRadius: 12, padding: '1rem' }}>
          <legend>{t('homepage.pageVisibilityLegend', 'Page visibility')}</legend>
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <input
                id="show-about-page"
                type="checkbox"
                checked={model.showAboutPage}
                onChange={(e) => onChange('showAboutPage', e.target.checked)}
              />
              {t('homepage.aboutPageVisible', 'About page visible')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <input
                id="show-portfolio-page"
                type="checkbox"
                checked={model.showPortfolioPage}
                onChange={(e) => onChange('showPortfolioPage', e.target.checked)}
              />
              {t('homepage.portfolioPageVisible', 'Portfolio page visible')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <input
                id="show-services-page"
                type="checkbox"
                checked={model.showServicesPage}
                onChange={(e) => onChange('showServicesPage', e.target.checked)}
              />
              {t('homepage.servicesPageVisible', 'Services page visible')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <input
                id="show-careers-page"
                type="checkbox"
                checked={model.showCareersPage}
                onChange={(e) => onChange('showCareersPage', e.target.checked)}
              />
              {t('homepage.careersPageVisible', 'Careers page visible')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <input
                id="show-news-page"
                type="checkbox"
                checked={model.showNewsPage}
                onChange={(e) => onChange('showNewsPage', e.target.checked)}
              />
              {t('homepage.newsPageVisible', 'News page visible')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <input
                id="show-contact-page"
                type="checkbox"
                checked={model.showContactPage}
                onChange={(e) => onChange('showContactPage', e.target.checked)}
              />
              {t('homepage.contactPageVisible', 'Contact page visible')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <input
                id="show-faq-page"
                type="checkbox"
                checked={model.showFaqPage}
                onChange={(e) => onChange('showFaqPage', e.target.checked)}
              />
              {t('homepage.faqPageVisible', 'FAQ page visible')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <input
                id="show-quote-page"
                type="checkbox"
                checked={model.showQuotePage}
                onChange={(e) => onChange('showQuotePage', e.target.checked)}
              />
              {t('homepage.quotePageVisible', 'Quote page visible')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <input
                id="show-legal-page"
                type="checkbox"
                checked={model.showLegalPage}
                onChange={(e) => onChange('showLegalPage', e.target.checked)}
              />
              {t('homepage.legalPageVisible', 'Legal page visible')}
            </label>
          </div>
        </fieldset>

        <fieldset style={{ border: '1px solid #d1d5db', borderRadius: 12, padding: '1rem' }}>
          <legend>{t('homepage.serviceCardsLegend')}</legend>
          {model.services.map((service, index) => (
            <div key={index} style={{ marginBottom: '1rem' }}>
              <label htmlFor={`service-title-${index}`}>{t('homepage.serviceTitleLabel', { index: index + 1 })}</label>
              <input
                id={`service-title-${index}`}
                className="form-control"
                value={service.title}
                onChange={(e) => onNestedChange('services', index, 'title', e.target.value)}
              />
              <label htmlFor={`service-description-${index}`} style={{ marginTop: '0.5rem', display: 'block' }}>{t('homepage.serviceDescriptionLabel', { index: index + 1 })}</label>
              <textarea
                id={`service-description-${index}`}
                className="form-control"
                rows={2}
                value={service.description}
                onChange={(e) => onNestedChange('services', index, 'description', e.target.value)}
              />
            </div>
          ))}
        </fieldset>

        <fieldset style={{ border: '1px solid #d1d5db', borderRadius: 12, padding: '1rem' }}>
          <legend>{t('homepage.testimonialsLegend')}</legend>
          {model.testimonials.map((testimonial, index) => (
            <div key={index} style={{ marginBottom: '1rem' }}>
              <label htmlFor={`testimonial-quote-${index}`}>{t('homepage.quoteLabel', { index: index + 1 })}</label>
              <textarea
                id={`testimonial-quote-${index}`}
                className="form-control"
                rows={2}
                value={testimonial.quote}
                onChange={(e) => onNestedChange('testimonials', index, 'quote', e.target.value)}
              />
              <label htmlFor={`testimonial-author-${index}`} style={{ marginTop: '0.5rem', display: 'block' }}>{t('homepage.authorLabel', { index: index + 1 })}</label>
              <input
                id={`testimonial-author-${index}`}
                className="form-control"
                value={testimonial.author}
                onChange={(e) => onNestedChange('testimonials', index, 'author', e.target.value)}
              />
            </div>
          ))}
        </fieldset>

        <div className="form-group">
          <label htmlFor="partners">{t('homepage.partnersLabel')}</label>
          <input
            id="partners"
            className="form-control"
            value={model.partners.join(', ')}
            onChange={(e) => onChange('partners', e.target.value.split(',').map((item) => item.trim()).filter(Boolean))}
          />
        </div>

        <fieldset style={{ border: '1px solid #d1d5db', borderRadius: 12, padding: '1rem' }}>
          <legend>{t('homepage.statsLegend')}</legend>
          {model.stats.map((stat, index) => (
            <div key={index} style={{ display: 'grid', gap: '0.5rem', marginBottom: '1rem' }}>
              <label htmlFor={`stat-label-${index}`}>{t('homepage.statLabelLabel', { index: index + 1 })}</label>
              <input
                id={`stat-label-${index}`}
                className="form-control"
                value={stat.label}
                onChange={(e) => onNestedChange('stats', index, 'label', e.target.value)}
              />
              <label htmlFor={`stat-value-${index}`}>{t('homepage.statValueLabel', { index: index + 1 })}</label>
              <input
                id={`stat-value-${index}`}
                className="form-control"
                value={stat.value}
                onChange={(e) => onNestedChange('stats', index, 'value', e.target.value)}
              />
            </div>
          ))}
        </fieldset>

        <fieldset style={{ border: '1px solid #d1d5db', borderRadius: 12, padding: '1rem' }}>
          <legend>{t('homepage.carouselLegend')}</legend>
          {model.carouselSlides.map((slide, index) => (
            <div key={index} style={{ marginBottom: '1rem' }}>
              <label htmlFor={`slide-src-${index}`}>{t('homepage.slideImagePathLabel', { index: index + 1 })}</label>
              <input
                id={`slide-src-${index}`}
                className="form-control"
                value={slide.src}
                onChange={(e) => onNestedChange('carouselSlides', index, 'src', e.target.value)}
              />
              <label htmlFor={`slide-alt-${index}`} style={{ marginTop: '0.5rem', display: 'block' }}>{t('homepage.slideAltTextLabel', { index: index + 1 })}</label>
              <input
                id={`slide-alt-${index}`}
                className="form-control"
                value={slide.alt}
                onChange={(e) => onNestedChange('carouselSlides', index, 'alt', e.target.value)}
              />
              <label htmlFor={`slide-title-${index}`} style={{ marginTop: '0.5rem', display: 'block' }}>{t('homepage.slideTitleLabel', { index: index + 1 })}</label>
              <input
                id={`slide-title-${index}`}
                className="form-control"
                value={slide.title}
                onChange={(e) => onNestedChange('carouselSlides', index, 'title', e.target.value)}
              />
              <label htmlFor={`slide-description-${index}`} style={{ marginTop: '0.5rem', display: 'block' }}>{t('homepage.slideDescriptionLabel', { index: index + 1 })}</label>
              <textarea
                id={`slide-description-${index}`}
                className="form-control"
                rows={2}
                value={slide.description}
                onChange={(e) => onNestedChange('carouselSlides', index, 'description', e.target.value)}
              />
            </div>
          ))}
        </fieldset>

        <div className="form-actions" style={{ gridColumn: '1 / -1', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" type="submit" disabled={loading}>
            {t('homepage.saveButton')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default HomePageEditor;
